import type { Language } from '@hero/schema';
import { arPack } from './packs/ar';
import { enPack } from './packs/en';
import { urPack } from './packs/ur';

/**
 * Reply-script guard: does an outbound reply stay in the thread's
 * language?
 *
 * The model writes the reply, so this is a check, not a rule it reads.
 * It works line by line, because the failure it catches is one foreign
 * line inside an otherwise correct reply — an Arabic «رقم جوال السائق؟»
 * pasted into an Urdu turn, a Roman-Urdu sentence in an Urdu-script
 * reply, a whole English paragraph in an Arabic one.
 *
 * What a reply may carry in another script, and is stripped before
 * counting: printed document / field names (the words on the card),
 * «quoted» values (a company or person name as printed), URLs, `code`,
 * and runs shorter than {@link MIN_FOREIGN_WORDS} words (a city, a
 * brand, a plate).
 */

export type DominantScript = 'arabic' | 'latin' | 'mixed' | 'none';

export type ReplyScriptIssue =
  | 'arabic_in_english_reply'
  | 'latin_in_arabic_reply'
  | 'urdu_in_arabic_reply'
  | 'arabic_in_urdu_reply'
  | 'latin_in_urdu_reply';

export interface ReplyScriptCheck {
  ok: boolean;
  issues: { issue: ReplyScriptIssue; line: string }[];
}

/** A foreign run shorter than this is a name, a city or a plate. */
export const MIN_FOREIGN_WORDS = 3;

const ARABIC_LETTER = new RegExp(`[${arPack.detection.scriptRanges.join('')}]`);
const LATIN_LETTER = new RegExp(`[${enPack.detection.scriptRanges.join('')}]`);

/**
 * Letters Urdu writes and Arabic does not (ٹ ڈ ڑ ں ے ہ ھ ک ی), from the
 * Urdu pack's `replyGuardChars`. A line of Arabic script without any of
 * them is Arabic, not Urdu.
 */
const URDU_LETTER = new RegExp(`[${urPack.detection.replyGuardChars}]`);

/** Printed names that stay as written in every language. */
const DOCUMENT_TERMS = [
  'وثيقة النقل',
  'وثيقة بلس',
  'رقم شركة النقل الموحد',
  'الرقم الموحد',
  'رقم نسخة البطاقة',
  'رقم النسخة',
  'السجل التجاري',
  'هوية صاحب العمل',
  'رخصة قيادة',
  'إقامة',
  'استمارة',
  'رخصة',
  'وثائق',
  'وثيقة',
  'هوية',
];

/** The article / preposition a printed name is often written with (الإقامة, بالاستمارة). */
const TERM_PREFIX = '(?:وال|بال|لل|ال)?';

/** Arabic-block punctuation and digits: «1، 2، 3» is not three Arabic words. */
const ARABIC_NON_LETTERS = /[\u060C\u061B\u061F\u0640\u0660-\u066D\u06D4\u06F0-\u06F9]/g;

const strippable = new RegExp(
  [
    'https?:\\/\\/\\S+',
    '`[^`]*`',
    '«[^»]*»',
    '"[^"]*"',
    ...DOCUMENT_TERMS.map(term => TERM_PREFIX + term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
  ].join('|'),
  'g',
);

const wordsOf = (line: string): { arabic: string[]; latin: string[] } => {
  const words = line
    .replace(strippable, ' ')
    .replace(ARABIC_NON_LETTERS, ' ')
    .split(/\s+/)
    .filter(Boolean);
  return {
    arabic: words.filter(word => ARABIC_LETTER.test(word)),
    latin: words.filter(word => LATIN_LETTER.test(word) && !ARABIC_LETTER.test(word)),
  };
};

/** The script most of the text's words are written in. */
export const dominantScript = (text: string): DominantScript => {
  const { arabic, latin } = wordsOf(text);
  const total = arabic.length + latin.length;
  if (total === 0) return 'none';
  if (arabic.length / total >= 0.8) return 'arabic';
  if (latin.length / total >= 0.8) return 'latin';
  return 'mixed';
};

const lineIssue = (line: string, language: Language): ReplyScriptIssue | null => {
  const { arabic, latin } = wordsOf(line);
  if (language === 'en') {
    return arabic.length >= MIN_FOREIGN_WORDS ? 'arabic_in_english_reply' : null;
  }
  if (language === 'ar') {
    if (latin.length >= MIN_FOREIGN_WORDS && latin.length > arabic.length) return 'latin_in_arabic_reply';
    const urduWords = arabic.filter(word => URDU_LETTER.test(word));
    return urduWords.length >= MIN_FOREIGN_WORDS ? 'urdu_in_arabic_reply' : null;
  }
  if (latin.length >= MIN_FOREIGN_WORDS && latin.length > arabic.length) return 'latin_in_urdu_reply';
  if (arabic.length >= MIN_FOREIGN_WORDS && !arabic.some(word => URDU_LETTER.test(word))) {
    return 'arabic_in_urdu_reply';
  }
  return null;
};

/** Checks every line of an outbound reply against the thread language. */
export const checkReplyScript = (text: string, language: Language): ReplyScriptCheck => {
  const issues = text
    .split(/\n+/)
    .map(line => line.trim())
    .filter(Boolean)
    .flatMap(line => {
      const issue = lineIssue(line, language);
      return issue ? [{ issue, line }] : [];
    });
  return { ok: issues.length === 0, issues };
};
